/**
 * Auditoría de datos y de permisos.
 */
import React, { useState, useEffect } from 'react';
import TrashIcon from '../../assets/icons/trash.svg';
import { ink, studio, StudioTabs, StudioIcon } from './studioUi';

const TYPE_LABEL = {
    permission: 'Permiso directo',
    group_member: 'Miembro de grupo',
    group_permission: 'Permiso de grupo',
    responsible: 'Responsable de reporte',
};

const AuditPanel = ({ API_URL, fetchWithAuth, userPermissions, setSubView, cargarTodo }) => {
    const initialAudit = userPermissions.admin_panel_audit_view ? 'data' : 'permissions';
    const [activeAudit, setActiveAudit] = useState(initialAudit);
    const [inconsistencies, setInconsistencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cleaning, setCleaning] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);

    useEffect(() => {
        setLoading(true);
        setInconsistencies([]);
        let endpoint = '';

        if (activeAudit === 'data' && userPermissions.admin_panel_audit_view) {
            endpoint = `${API_URL}/admin/audit-data`;
        } else if (activeAudit.startsWith('permissions') && userPermissions.admin_panel_comprehensive_audit_view) {
            endpoint = `${API_URL}/admin/comprehensive-permissions-audit`;
        }

        if (endpoint) {
            fetchWithAuth(endpoint)
                .then((res) => res.json())
                .then((data) => {
                    if (data.success) setInconsistencies(data.inconsistencies || []);
                    else {
                        console.error(`Error en la respuesta de la auditoría de ${activeAudit}:`, data.error);
                        setInconsistencies([]);
                    }
                })
                .catch((err) => {
                    console.error(`Fallo al cargar auditoría de ${activeAudit}:`, err.message);
                    setInconsistencies([]);
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, [activeAudit, API_URL, fetchWithAuth, userPermissions]);

    const handleCleanInconsistency = async (item, confirm = true) => {
        if (confirm && !window.confirm(`¿Estás seguro de que quieres limpiar esta inconsistencia? Esto eliminará el registro: ${item.description}`)) {
            return;
        }

        setCleaning(true);
        try {
            let url = '';
            switch (item.type) {
                case 'permission':
                    url = `${API_URL}/admin/delete-permission/${item.id}`;
                    break;
                case 'group_member':
                    url = `${API_URL}/admin/groups/${item.groupId}/members/${item.userId}`;
                    break;
                case 'group_permission':
                    url = `${API_URL}/admin/groups/${item.groupId}/permissions/${item.reporteId}`;
                    break;
                case 'responsible':
                    url = `${API_URL}/admin/reportes-maestros/${item.reportId}/responsibles/${item.responsibleId}`;
                    break;
                default:
                    alert('Tipo de inconsistencia no reconocido para limpieza.');
                    setCleaning(false);
                    return;
            }

            const res = await fetchWithAuth(url, { method: 'DELETE' });
            if (res.ok) {
                if (confirm) {
                    alert('Inconsistencia limpia con éxito.');
                    setActiveAudit((prev) => (prev === 'permissions' ? 'permissions_reload' : 'permissions'));
                }
            } else {
                const errorData = await res.json().catch(() => ({}));
                alert(`Error al limpiar inconsistencia: ${errorData.error || 'Error desconocido'}`);
            }
        } catch (error) {
            alert(`Error de conexión al limpiar inconsistencia: ${error.message}`);
        } finally {
            if (confirm) setCleaning(false);
        }
    };

    useEffect(() => {
        if (activeAudit === 'permissions_reload') {
            setActiveAudit('permissions');
        }
    }, [activeAudit]);

    const handleCleanAll = async () => {
        if (!window.confirm(`¿Estás seguro de que quieres limpiar TODAS las ${inconsistencies.length} inconsistencias listadas? Esta acción no se puede deshacer.`)) {
            return;
        }

        setCleaning(true);
        for (const item of inconsistencies) {
            await handleCleanInconsistency(item, false);
        }
        setCleaning(false);
        alert('Proceso de limpieza completado. Recargando datos...');
        setActiveAudit((prev) => (prev === 'permissions' ? 'permissions_reload' : 'permissions'));
        if (typeof cargarTodo === 'function') cargarTodo();
    };

    const auditTabs = [
        userPermissions.admin_panel_audit_view && { id: 'data', label: 'Datos de reportes', count: activeAudit.startsWith('data') ? inconsistencies.length : undefined },
        userPermissions.admin_panel_comprehensive_audit_view && { id: 'permissions', label: 'Permisos', count: activeAudit.startsWith('permissions') ? inconsistencies.length : undefined },
    ].filter(Boolean);

    const renderDataAudit = () => (
        <>
            <p style={{ ...studio.hint, marginBottom: 16, maxWidth: 640 }}>
                Reportes con cliente o proyecto inválido, o campos requeridos faltantes. Corregir abre la biblioteca.
            </p>
            {inconsistencies.length === 0 ? (
                <div style={{ ...studio.empty, color: '#137333' }}>No hay inconsistencias de datos.</div>
            ) : (
                <div style={{ overflow: 'auto' }}>
                    <table style={studio.table}>
                        <thead>
                            <tr>
                                <th style={studio.th}>Reporte</th>
                                <th style={studio.th}>Cliente</th>
                                <th style={studio.th}>Proyecto</th>
                                <th style={studio.th}>Problema</th>
                                <th style={studio.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {inconsistencies.map((item) => (
                                <tr
                                    key={item.id}
                                    style={hoveredRow === item.id ? studio.trHover : undefined}
                                    onMouseEnter={() => setHoveredRow(item.id)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                >
                                    <td style={{ ...studio.td, fontWeight: 600 }}>{item.reporteNombre}</td>
                                    <td style={{ ...studio.td, color: ink.muted }}>{item.cliente || '—'}</td>
                                    <td style={{ ...studio.td, color: ink.muted }}>{item.proyecto || '—'}</td>
                                    <td style={studio.td}>
                                        <ul style={{ margin: 0, paddingLeft: 18, color: ink.danger }}>
                                            {item.issues.map((issue, idx) => <li key={idx}>{issue}</li>)}
                                        </ul>
                                    </td>
                                    <td style={studio.td}>
                                        <button type="button" onClick={() => setSubView('biblioteca')} style={studio.ghostBtn}>Corregir</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );

    const renderPermissionsAudit = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <p style={{ ...studio.hint, margin: 0, maxWidth: 560 }}>
                    Integridad de permisos directos, de grupo y responsables: que usuario, reporte, cliente y proyecto sigan existiendo.
                </p>
                {inconsistencies.length > 0 && userPermissions.permissions_delete && (
                    <button type="button" onClick={handleCleanAll} style={studio.dangerBtn} disabled={cleaning}>
                        Limpiar todo ({inconsistencies.length})
                    </button>
                )}
            </div>
            {inconsistencies.length === 0 ? (
                <div style={{ ...studio.empty, color: '#137333' }}>No hay inconsistencias de permisos.</div>
            ) : (
                <div style={{ overflow: 'auto' }}>
                    <table style={studio.table}>
                        <thead>
                            <tr>
                                <th style={studio.th}>Tipo</th>
                                <th style={studio.th}>Descripción</th>
                                <th style={studio.th}>Problema</th>
                                <th style={studio.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {inconsistencies.map((item) => (
                                <tr
                                    key={item.id}
                                    style={hoveredRow === item.id ? studio.trHover : undefined}
                                    onMouseEnter={() => setHoveredRow(item.id)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                >
                                    <td style={studio.td}>{TYPE_LABEL[item.type] || item.type}</td>
                                    <td style={studio.td}>{item.description}</td>
                                    <td style={studio.td}>
                                        <ul style={{ margin: 0, paddingLeft: 18, color: ink.danger }}>
                                            {item.issues.map((issue, idx) => <li key={idx}>{issue}</li>)}
                                        </ul>
                                    </td>
                                    <td style={studio.td}>
                                        <button
                                            type="button"
                                            onClick={() => handleCleanInconsistency(item)}
                                            style={studio.iconBtnDanger}
                                            disabled={cleaning}
                                            title="Limpiar"
                                        >
                                            <StudioIcon src={TrashIcon} alt="Limpiar" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {auditTabs.length > 1 && (
                <StudioTabs
                    items={auditTabs}
                    value={activeAudit.startsWith('permissions') ? 'permissions' : 'data'}
                    onChange={setActiveAudit}
                />
            )}
            <div style={studio.card}>
                {loading ? (
                    <div style={studio.empty}>Cargando auditoría…</div>
                ) : (
                    activeAudit.startsWith('data') ? renderDataAudit() : renderPermissionsAudit()
                )}
            </div>
        </div>
    );
};

export default AuditPanel;
